import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "plumbing-seo-colorado",
  metaTitle: "Plumbing SEO in Colorado | Local SEO for Plumbers | Colorado Web Impressions",
  metaDescription:
    "Plumbing SEO across Colorado — Map Pack rankings, emergency lead capture, and content built for high-intent plumbing searches. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Plumbing marketing", to: "/plumbing-marketing-colorado" },
  ],
  currentLabel: "Plumbing SEO Colorado",
  eyebrow: "Plumbing SEO — Colorado",
  h1: (
    <>
      Plumbing <span className="text-brand-gold">SEO</span> in Colorado
    </>
  ),
  heroDescription:
    "Win the high-intent searches that actually book jobs — water heater replacements, drain emergencies, and slab-leak repairs across every Colorado market.",
  intro:
    "Most plumbing SEO is generic — a 'plumber near me' page and hope. We build dedicated ranking pages for the jobs that actually pay: tankless water heater install, slab leak detection, sewer line repair, drain cleaning, and re-pipes. Each page targets the way Coloradans actually search and is wired into your Google Business Profile so emergency calls route to you, not the national lead-gen aggregators.",
  definition: {
    term: "Plumbing SEO in Colorado",
    body: "Plumbing SEO is local search optimization that ranks plumbing companies for high-intent jobs like 'water heater repair Denver', 'slab leak Colorado Springs', or 'emergency plumber near me.' It pairs Map Pack work with deep service-page content and structured review velocity.",
  },
  whatWeDo: [
    { title: "One ranking page per service line", body: "Water heater, drain cleaning, sewer line, slab leak, re-pipe, gas line — each gets its own dedicated, FAQ-rich page." },
    { title: "Map Pack work in every Colorado service area", body: "Colorado Springs, Denver, Fort Collins, Pueblo, Castle Rock, Parker — neighborhood-level targeting where the aggregators don't dominate." },
    { title: "Emergency lead capture", body: "Sticky click-to-call, after-hours messaging, and conversion-tested CTAs that convert 2 a.m. burst-pipe searches into booked jobs." },
    { title: "Review velocity tied to dispatch", body: "Structured review-request flow tied to ServiceTitan or your FSM so reviews come in steadily — not in spikes that look fake to Google." },
    { title: "Schema for plumbing trust signals", body: "LocalBusiness, Service, FAQPage, and Review schema — plus master plumber license, bonded/insured signals, and warranty terms surfaced on-page." },
    { title: "Mobile speed and conversion", body: "Plumbing search is 80%+ mobile and emergency-intent. Hand-coded pages, sub-2s load, click-to-call above the fold." },
  ],
  whyThisMarket: [
    {
      heading: "Plumbing search is emergency-driven and unforgiving",
      body: "When a Colorado homeowner has water in the basement at 11 p.m., they call the first three plumbers in the Map Pack. If you're not there, you didn't exist. Map Pack ranking is the entire game.",
    },
    {
      heading: "National aggregators dominate generic terms",
      body: "Angi, HomeAdvisor, and Networx outrank most independent Colorado plumbers on city-level keywords. We win by going deeper — neighborhood pages, specific service pages, and schema the aggregators don't bother with.",
    },
    {
      heading: "Reviews compound faster in plumbing than almost any vertical",
      body: "Plumbing has the highest review-to-rank correlation we've measured. A Colorado plumber with 80 fresh reviews will routinely outrank one with 400 stale reviews from years past. We make review velocity a system, not an afterthought.",
    },
  ],
  relatedLinks: [
    { to: "/plumbing-marketing-colorado", label: "Plumbing marketing in Colorado — full hub" },
    { to: "/colorado-springs-seo", label: "How our SEO process works" },
    { to: "/colorado-springs-google-maps", label: "Local SEO & Google Maps overview" },
    { to: "/plumbing-web-design-colorado", label: "Plumbing web design in Colorado" },
    { to: "/plumbing-google-maps-colorado", label: "Plumbing Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado plumbing case studies" },
    { to: "/seo", label: "Our SEO service (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How long does plumbing SEO take to work in Colorado?", a: "Most Colorado plumbers see Map Pack movement in 60–90 days and stronger organic call volume by month 4–6. Plumbing is competitive but moves faster than most verticals because the review signal is so strong." },
    { q: "Should plumbers invest in SEO or PPC first?", a: "If you need calls this week, run Google Ads (especially Local Services Ads). If you want lead flow that doesn't shut off when budgets get cut, build SEO. Most successful Colorado plumbing companies run all three — LSAs, Search Ads, and SEO — together." },
    { q: "Do you do Local Services Ads (LSA) management?", a: "Yes — we manage LSA optimization, dispute weak leads, and tune your service categories. LSAs and SEO compound when run together because both feed your Google Business Profile signal." },
    { q: "Do you only serve Colorado Springs plumbers?", a: "No — we serve plumbing companies across all of Colorado: Denver, Fort Collins, Boulder, Pueblo, Castle Rock, Parker, and the Front Range. We're Colorado-only by design." },
  ],
  serviceName: "Plumbing SEO in Colorado",
  serviceDescription:
    "Local SEO and Map Pack optimization for plumbing companies across Colorado. Service-line pages, review velocity, and emergency lead capture.",
  areaServed: "Colorado",
  // SEO: consolidate ranking signal onto the /plumbing-marketing-colorado flagship hub.
  canonicalOverride: "https://coloradowebimpressions.com/plumbing-marketing-colorado",
};

export const Route = createFileRoute("/plumbing-seo-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
